import { useMemo } from 'react';
import { communityTrpc } from '@org/clients/community';
import {
  EventAgenda,
  badgeKindForEventKind,
  type AgendaItem,
  type EventAgendaLabels,
} from '../custom/event-agenda';

export interface ConnectedUpcomingEventsProps {
  limit?: number;
  /**
   * Origin to prefix the community-relative event hrefs with (e.g. the
   * community app URL when rendering from another app). Empty = same origin.
   */
  hrefBase?: string;
  title?: string;
  emptyLabel?: string;
  viewAllHref?: string;
  viewAllLabel?: string;
  /** Overrides for the remaining agenda labels (today/tomorrow/times/kinds). */
  labels?: Partial<Omit<EventAgendaLabels, 'title' | 'empty' | 'seeAll'>>;
  className?: string;
}

/**
 * Community events rail, participant-stack style: each row shows the host's
 * avatar + how many people are invited. The server scopes the list to events
 * the signed-in user can actually see.
 */
export function ConnectedUpcomingEvents({
  limit = 5,
  hrefBase = '',
  title = 'Upcoming events',
  emptyLabel = 'No upcoming events',
  viewAllHref,
  viewAllLabel = 'View all',
  labels: labelOverrides,
  className,
}: ConnectedUpcomingEventsProps) {
  const { data, isLoading } = communityTrpc.events.upcoming.useQuery(
    { limit },
    { staleTime: 60_000 }
  );

  const items = useMemo<AgendaItem[]>(() => {
    const nowMs = Date.now();
    return (data ?? []).map((event) => {
      const startMs = new Date(event.startsAt).getTime();
      const endMs = event.endsAt ? new Date(event.endsAt).getTime() : startMs + 60 * 60 * 1000;
      return {
        id: event.id,
        title: event.title,
        startTime: event.startsAt,
        endTime: event.endsAt ?? null,
        badgeKind: badgeKindForEventKind(event.eventKind),
        host: { name: event.hostName ?? null, avatarUrl: event.hostAvatarUrl ?? null },
        othersCount: event.participantsCount ?? 0,
        isLive: startMs <= nowMs && nowMs < endMs,
        href: `${hrefBase}${event.href}`,
      };
    });
  }, [data, hrefBase]);

  const labels: EventAgendaLabels = {
    title,
    empty: emptyLabel,
    seeAll: viewAllLabel,
    today: 'Today',
    tomorrow: 'Tomorrow',
    startsInMinutes: (minutes) => `Starts in ${minutes} min`,
    startsAt: (time) => `Starts at ${time}`,
    kind: { group: 'Group class', personal: 'Personal', event: 'Event' },
    ...labelOverrides,
  };

  return (
    <EventAgenda
      items={items}
      isLoading={isLoading}
      labels={labels}
      seeAllHref={viewAllHref ? `${hrefBase}${viewAllHref}` : undefined}
      className={className}
    />
  );
}
